import React, { useCallback, useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Users,
  Search,
  Plus,
  Edit,
  Trash2,
  Shield,
  UserCheck,
  BarChart2,
  History,
  RotateCcw,
  XCircle,
  Clock,
  Key,
} from "lucide-react";
import { format } from "date-fns";

import Loader from "../../../loader/Loader";
import AddUsers from "../UserModule/AddUsers";
import AuditInfoModal, { buildAuditRows } from "../../../shared/AuditInfoModal";
import Pagination from "../../Pagination";
import { domain } from "../../../../security";
import { selectUserID } from "../../../../redux/IchthusSlice";

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return format(new Date(dateString), "MMM dd, yyyy");
};

// ==========================================
// HISTORY MODAL
// ==========================================
const DeletedUsersModal = ({ isOpen, onClose, onRestore }) => {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [auditItem, setAuditItem] = useState(null);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${domain}/api/Users/history?search=${search}`,
      );
      setHistoryData(response.data);
    } catch {
      toast.error("Failed to load user history.");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    if (isOpen) {
      fetchHistory();
      setCurrentPage(1);
    }
  }, [isOpen, fetchHistory]);

  if (!isOpen) return null;

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = historyData.slice(indexOfFirst, indexOfLast);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-slate-100 flex justify-between items-center">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <div className="p-2 bg-red-50 rounded-full text-red-500">
              <Trash2 size={18} />
            </div>
            Deleted Users History
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition"
          >
            <XCircle size={22} />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3 bg-slate-50/50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search deleted users..."
              className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-red-100 focus:border-red-400 text-sm outline-none transition-all"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 bg-slate-50/50">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader />
            </div>
          ) : historyData.length === 0 ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-100 mb-3">
                <History size={20} className="text-slate-400" />
              </div>
              <p className="text-slate-500 text-sm">No deleted users found.</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden sm:block bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <table className="w-full text-sm text-left text-slate-600">
                  <thead className="text-xs text-slate-500 uppercase bg-slate-50">
                    <tr>
                      <th className="px-4 py-3">Username</th>
                      <th className="px-4 py-3">Employee</th>
                      <th className="px-4 py-3">Deleted By</th>
                      <th className="px-4 py-3">Deleted Date</th>
                      <th className="px-4 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {currentItems.map((item) => (
                      <tr key={item.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-800">
                          <div className="flex items-center gap-1">
                            @{item.userName}
                            <button
                              onClick={() => setAuditItem(item)}
                              className="text-amber-500 hover:text-amber-700 ml-1"
                              title="View Audit Info"
                            >
                              <Clock size={14} />
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">
                          {item.employeeName || "N/A"}
                        </td>
                        <td className="px-4 py-3 text-xs font-medium text-red-500">
                          {item.deletedByFullName || "Unknown"}
                        </td>
                        <td className="px-4 py-3 text-xs text-red-400">
                          {formatDate(item.deletedDate)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={async () => {
                              await onRestore(item.id);
                              fetchHistory();
                            }}
                            className="text-green-600 hover:text-green-700 font-semibold text-xs flex items-center gap-1 ml-auto"
                          >
                            <RotateCcw size={14} /> Restore
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile */}
              <div className="sm:hidden flex flex-col gap-3">
                {currentItems.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-slate-800">
                        @{item.userName}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setAuditItem(item)}
                          className="p-2 bg-amber-50 text-amber-500 rounded-lg hover:bg-amber-100"
                        >
                          <Clock size={14} />
                        </button>
                        <button
                          onClick={async () => {
                            await onRestore(item.id);
                            fetchHistory();
                          }}
                          className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100"
                        >
                          <RotateCcw size={16} />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500">
                      Deleted by:{" "}
                      <span className="font-medium text-red-500">
                        {item.deletedByFullName || "Unknown"}
                      </span>{" "}
                      on {formatDate(item.deletedDate)}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-3">
                <Pagination
                  itemsPerPage={itemsPerPage}
                  totalItems={historyData.length}
                  currentPage={currentPage}
                  paginate={(p) => setCurrentPage(p)}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <AuditInfoModal
        isOpen={!!auditItem}
        onClose={() => setAuditItem(null)}
        title={auditItem?.userName}
        rows={buildAuditRows(auditItem)}
      />
    </div>
  );
};

// ==========================================
// MAIN COMPONENT
// ==========================================
const AllUsers = () => {
  const currentUserID = useSelector(selectUserID);

  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [auditItem, setAuditItem] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);

  // Reset Password State
  const [isResetModalVisible, setIsResetModalVisible] = useState(false);
  const [userToReset, setUserToReset] = useState(null);
  const [resetting, setResetting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${domain}/api/Users`);
      setUserData(response.data);
    } catch {
      toast.error("Failed to fetch users.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const lower = searchTerm.toLowerCase();
    setFilteredUsers(
      userData.filter(
        (u) =>
          (u.employeeName || "").toLowerCase().includes(lower) ||
          (u.userName || "").toLowerCase().includes(lower),
      ),
    );
    setCurrentPage(1);
  }, [searchTerm, userData]);

  const handleSoftDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await axios.put(
        `${domain}/api/Users/soft-delete/${id}`,
        { userID: currentUserID },
        { headers: { "Content-Type": "application/json" } },
      );
      toast.success("User moved to history!");
      fetchData();
    } catch {
      toast.error("Failed to delete user.");
    }
  };

  const handleRestore = async (id) => {
    try {
      await axios.put(
        `${domain}/api/Users/restore/${id}`,
        { userID: currentUserID },
        { headers: { "Content-Type": "application/json" } },
      );
      toast.success("User restored successfully!");
      fetchData();
    } catch {
      toast.error("Failed to restore user.");
    }
  };

  const handleResetPassword = async () => {
    if (!userToReset) return;

    try {
      setResetting(true);
      // We pass the same required properties similar to edit but enforcing "password"
      const payload = {
        EmployeeId: Number(userToReset.employeeId),
        RoleId: Number(userToReset.roleId),
        UserID: currentUserID,
        Password: "password",
      };

      await axios.put(`${domain}/api/Users/${userToReset.id}`, payload);
      toast.success(
        `Password for @${userToReset.userName} successfully reset to default.`,
      );
      setIsResetModalVisible(false);
      setUserToReset(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to reset user password.");
    } finally {
      setResetting(false);
    }
  };

  const indexOfLast = currentPage * usersPerPage;
  const indexOfFirst = indexOfLast - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirst, indexOfLast);

  return (
    <div className="min-h-screen pb-12">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-20">
        <div className="max-w-[95%] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                <Users className="text-indigo-600" /> User Management
              </h1>
              <p className="text-sm text-slate-500 mt-1 hidden sm:block">
                Manage system users, roles, and account access permissions.
              </p>
            </div>
            <div className="flex gap-3 w-full md:w-auto">
              <button
                onClick={() => setIsHistoryModalVisible(true)}
                className="flex-1 md:flex-none flex justify-center items-center gap-2 bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100 text-sm font-medium px-4 py-2.5 rounded-xl transition-all"
              >
                <History size={18} /> History
              </button>
              <button
                onClick={() => {
                  setUserToEdit(null);
                  setIsModalVisible(true);
                }}
                className="flex-1 md:flex-none flex justify-center items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-5 py-2.5 rounded-xl shadow-md shadow-indigo-200 transition-all"
              >
                <Plus size={18} /> Add New User
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[95%] mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        {/* Stats & Search */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex items-center gap-4">
            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
              <BarChart2 size={24} />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                Active Users
              </p>
              <p className="text-2xl font-bold text-slate-800">
                {filteredUsers.length}
              </p>
            </div>
          </div>
          <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex items-center">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name or username..."
                className="w-full pl-10 pr-4 py-2 text-sm border-none ring-1 ring-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all placeholder:text-slate-400"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader />
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <table className="w-full text-sm text-left text-slate-600">
                <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 font-semibold">
                      Employee Details
                    </th>
                    <th className="px-6 py-4 font-semibold">Username</th>
                    <th className="px-6 py-4 font-semibold">Role</th>
                    <th className="px-6 py-4 font-semibold text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {currentUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-slate-50 transition">
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="font-semibold text-slate-800">
                            {user.employeeName}
                          </span>
                          <span className="text-xs text-slate-400">
                            ID: {user.employeeId}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="bg-slate-100 px-2 py-1 rounded text-slate-700 font-medium">
                          @{user.userName}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-slate-700">
                          <Shield size={14} className="text-indigo-500" />
                          {user.roleName}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* Audit */}
                          <button
                            onClick={() => setAuditItem(user)}
                            className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                            title="View Audit Info"
                          >
                            <Clock size={18} />
                          </button>
                          {/* Reset Password */}
                          <button
                            onClick={() => {
                              setUserToReset(user);
                              setIsResetModalVisible(true);
                            }}
                            className="p-2 text-slate-400 hover:text-sky-600 hover:bg-sky-50 rounded-lg transition-colors"
                            title="Reset Password to Default"
                          >
                            <Key size={18} />
                          </button>
                          {/* Edit */}
                          <button
                            onClick={() => {
                              setUserToEdit(user);
                              setIsModalVisible(true);
                            }}
                            className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="Edit User"
                          >
                            <Edit size={18} />
                          </button>
                          {/* Soft Delete */}
                          <button
                            onClick={() => handleSoftDelete(user.id)}
                            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete User"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {currentUsers.length === 0 && (
                    <tr>
                      <td
                        colSpan="4"
                        className="px-6 py-10 text-center text-slate-400"
                      >
                        No users found matching your search.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="p-4 border-t border-slate-100">
              <Pagination
                itemsPerPage={usersPerPage}
                totalItems={filteredUsers.length}
                currentPage={currentPage}
                paginate={(p) => setCurrentPage(p)}
              />
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden flex flex-col gap-3">
              {currentUsers.map((user) => (
                <div
                  key={user.id}
                  className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 flex flex-col gap-3"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-2.5">
                      <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
                        <UserCheck size={16} />
                      </div>
                      <div>
                        <span className="block font-bold text-slate-800">
                          {user.employeeName}
                        </span>
                        <span className="text-xs text-slate-400">
                          @{user.userName}
                        </span>
                      </div>
                    </div>
                    <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-[10px] uppercase font-bold border border-slate-200">
                      {user.roleName}
                    </span>
                  </div>

                  <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-50 flex-wrap">
                    <button
                      onClick={() => setAuditItem(user)}
                      className="text-xs font-semibold text-amber-500 hover:text-amber-700 flex items-center gap-1"
                    >
                      <Clock size={14} /> Audit
                    </button>
                    <button
                      onClick={() => {
                        setUserToReset(user);
                        setIsResetModalVisible(true);
                      }}
                      className="text-xs font-semibold text-sky-600 hover:text-sky-800 flex items-center gap-1"
                    >
                      <Key size={14} /> Reset Pwd
                    </button>
                    <button
                      onClick={() => {
                        setUserToEdit(user);
                        setIsModalVisible(true);
                      }}
                      className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                    >
                      <Edit size={14} /> Edit
                    </button>
                    <button
                      onClick={() => handleSoftDelete(user.id)}
                      className="text-xs font-semibold text-red-500 hover:text-red-700 flex items-center gap-1"
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                </div>
              ))}
              {currentUsers.length === 0 && (
                <div className="text-center py-10 text-slate-400 bg-white rounded-lg border border-slate-200">
                  No users found.
                </div>
              )}
              <div className="mt-2 pb-6">
                <Pagination
                  itemsPerPage={usersPerPage}
                  totalItems={filteredUsers.length}
                  currentPage={currentPage}
                  paginate={(p) => setCurrentPage(p)}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {isModalVisible && (
        <AddUsers
          onClose={() => {
            setIsModalVisible(false);
            fetchData();
          }}
          refreshData={fetchData}
          userToEdit={userToEdit}
        />
      )}

      {/* Reset Password Modal */}
      {isResetModalVisible && userToReset && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm flex flex-col overflow-hidden p-6 text-center">
            <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-sky-100 mb-4 text-sky-600">
              <Key size={28} />
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-2">
              Reset Password
            </h3>
            <p className="text-sm text-slate-500 mb-6">
              Are you sure you want to reset the password for{" "}
              <span className="font-semibold text-slate-700">
                @{userToReset.userName}
              </span>{" "}
              to the default password{" "}
              <span className="font-mono font-bold bg-slate-100 px-1.5 py-0.5 rounded text-slate-800">
                password
              </span>
              ?
            </p>
            <div className="flex gap-3 w-full">
              <button
                onClick={() => setIsResetModalVisible(false)}
                className="flex-1 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition font-medium"
                disabled={resetting}
              >
                Cancel
              </button>
              <button
                onClick={handleResetPassword}
                className="flex-1 px-4 py-2.5 bg-sky-600 text-white rounded-xl hover:bg-sky-700 transition font-medium flex justify-center items-center"
                disabled={resetting}
              >
                {resetting ? "Resetting..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      <DeletedUsersModal
        isOpen={isHistoryModalVisible}
        onClose={() => {
          setIsHistoryModalVisible(false);
          fetchData();
        }}
        onRestore={handleRestore}
      />

      {/* Audit Modal */}
      <AuditInfoModal
        isOpen={!!auditItem}
        onClose={() => setAuditItem(null)}
        title={auditItem?.userName}
        rows={buildAuditRows(auditItem)}
      />
    </div>
  );
};

export default AllUsers;
